import type { CSSProperties } from "react";
import type { ArticleBlockTypography, ArticleImageTextBlock } from "./models";
import {
  boxCss,
  isCssLength,
  lengthBox,
  lengthOrToken,
  SPACING,
} from "@/purpose-sections/length";
import { purposeStyle } from "@/purpose-sections/style";
import { preciseTypographyValue } from "@/purpose-sections/typography";

function responsive(
  style: Record<string, string>,
  name: string,
  raw: unknown,
  parse: (value: string) => string | undefined,
) {
  if (typeof raw !== "string") return;
  const [mobile, desktop = mobile] = raw
    .split("|")
    .map((value) => value.trim());
  const small = parse(mobile);
  const large = parse(desktop);
  if (small !== undefined) style[name] = small;
  if (large !== undefined) style[`${name}-desktop`] = large;
}

export function articleBlockTypographyStyle(
  input: ArticleBlockTypography | undefined,
  area: "text" | "heading" | "subheading" = "heading",
): CSSProperties {
  if (!input || typeof input !== "object") return {};
  const sharedArea = area === "subheading" ? "eyebrow" : area;
  // Only expose the shared type scale, weight and tracking, not arbitrary surface settings.
  const parsed = purposeStyle({
    styles: {
      [sharedArea]: {
        fontSize: input.fontSize,
        fontWeight: input.fontWeight,
        letterSpacing: input.letterSpacing,
      },
    },
  });
  const style: Record<string, string> = {};
  for (const [name, value] of Object.entries(parsed))
    if (value !== undefined) style[name] = String(value);
  responsive(
    style,
    `--pf-${sharedArea}-line-height`,
    input.lineHeight,
    (value) => preciseTypographyValue(value, "line-height"),
  );
  responsive(style, `--a-${area}-margin`, input.margin, lengthBox);
  if (input.fontRole === "body" || input.fontRole === "heading")
    style[`--a-${area}-font-family`] = `var(--${input.fontRole}-font-family)`;
  return style as CSSProperties;
}

/** All declarations are built from shared validated values, never customer CSS. */
export function articleImageTextStyle(
  block: ArticleImageTextBlock,
): CSSProperties {
  const style: Record<string, string> = {};
  if (
    typeof block.imageWidth === "string" &&
    /^\d+(?:\.\d+)?%$/.test(block.imageWidth.trim())
  ) {
    const share = parseFloat(block.imageWidth);
    if (share >= 10 && share <= 90) {
      style["--a-image-track"] = `${share}fr`;
      style["--a-copy-track"] = `${100 - share}fr`;
    }
  }
  responsive(style, "--a-image-text-gap", block.gap, lengthOrToken);
  responsive(style, "--a-row-margin", block.margin, (value) => {
    const values = value.split(/\s+/);
    if (
      values.length > 4 ||
      values.some((part) => !Object.hasOwn(SPACING, part) && !isCssLength(part))
    )
      return undefined;
    const box = boxCss(value).mobile;
    return box && `${box.top} ${box.right} ${box.bottom} ${box.left}`;
  });
  responsive(style, "--a-copy-padding", block.contentPadding, lengthBox);
  responsive(style, "--a-media-padding", block.mediaPadding, lengthBox);
  responsive(style, "--a-image-height", block.imageHeight, (value) =>
    value === "auto"
      ? value
      : /%$/.test(value)
        ? undefined
        : lengthOrToken(value),
  );
  for (const area of ["text", "heading", "subheading"] as const)
    Object.assign(
      style,
      articleBlockTypographyStyle(block.styles?.[area], area),
    );
  return style as CSSProperties;
}
